/**
 * Public /home lookup endpoints — ALL schema-validated end-to-end as of
 * 2026-07-28 (the "wire as their domains are touched" exceptions are
 * gone; this closed the product/home/search domain's tail):
 * - listing categories/sub-categories (inventory wizard pickers,
 *   CategoryGrid id→name lookups)
 * - store-amenity catalog (filter options)
 * - vendorList (search filter's vendor dropdown)
 *
 * getTrustedVendors was DELETED in the same pass — zero consumers
 * anywhere (the home trusted-vendors section it once fed is gone).
 *
 * Full wire paths, no baseUrl — matches listingsApi and the shared
 * route-const pattern.
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import type { z } from "zod";
import {
  CATALOG_LOOKUP_ROUTES,
  ListingCategoriesEnvelopeSchema,
  ListingCategoriesResponseSchema,
  unwrapListingCategories,
  ListingSubCategoriesEnvelopeSchema,
  ListingSubCategoriesResponseSchema,
  unwrapListingSubCategories,
} from "@gearghost/api-schemas/listing";
import {
  PUBLIC_VENDOR_ROUTES,
  StoreAmenityCatalogEnvelopeSchema,
  StoreAmenityCatalogResponseSchema,
  unwrapStoreAmenityCatalog,
  VendorListEnvelopeSchema,
  VendorListResponseSchema,
  unwrapVendorList,
} from "@gearghost/api-schemas/vendor";
import { NOT_FOUND_ROUTES, type TrackNotFoundRequest } from "@gearghost/api-schemas/notFound";
import axiosBaseQuery from "./axiosBaseQuery";
import { onSchemaFailure } from "./onSchemaFailure";

export const homeApi = createApi({
  reducerPath: "homeApi",
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ["ListingCategories", "ListingSubCategories", "Vendors", "Amenities"],
  endpoints: (builder) => ({
    getListingCategories: builder.query<
      z.infer<typeof ListingCategoriesResponseSchema>,
      void
    >({
      query: () => CATALOG_LOOKUP_ROUTES.listingCategories,
      rawResponseSchema: ListingCategoriesEnvelopeSchema,
      transformResponse: unwrapListingCategories,
      responseSchema: ListingCategoriesResponseSchema,
      providesTags: ["ListingCategories"],
    }),
    getListingSubCategories: builder.query<
      z.infer<typeof ListingSubCategoriesResponseSchema>,
      void
    >({
      query: () => CATALOG_LOOKUP_ROUTES.listingSubCategories,
      rawResponseSchema: ListingSubCategoriesEnvelopeSchema,
      transformResponse: unwrapListingSubCategories,
      responseSchema: ListingSubCategoriesResponseSchema,
      providesTags: ["ListingSubCategories"],
    }),
    getVendorList: builder.query<z.infer<typeof VendorListResponseSchema>, void>({
      query: () => PUBLIC_VENDOR_ROUTES.vendorList,
      rawResponseSchema: VendorListEnvelopeSchema,
      transformResponse: unwrapVendorList,
      responseSchema: VendorListResponseSchema,
      providesTags: ["Vendors"],
    }),
    getAmenities: builder.query<
      z.infer<typeof StoreAmenityCatalogResponseSchema>,
      void
    >({
      query: () => PUBLIC_VENDOR_ROUTES.amenities,
      rawResponseSchema: StoreAmenityCatalogEnvelopeSchema,
      transformResponse: unwrapStoreAmenityCatalog,
      responseSchema: StoreAmenityCatalogResponseSchema,
      providesTags: ["Amenities"],
    }),
    // The not-found page counts each visit (NOT_FOUND_ROUTES.track).
    // no responseSchema — nothing reads the answer; the visit is only counted.
    trackNotFound: builder.mutation<void, TrackNotFoundRequest>({
      query: (body) => ({
        url: NOT_FOUND_ROUTES.track,
        method: "POST",
        body,
      }),
      transformResponse: () => undefined,
    }),
  }),
});

export const {
  useGetListingCategoriesQuery,
  useGetListingSubCategoriesQuery,
  useGetVendorListQuery,
  useGetAmenitiesQuery,
  useTrackNotFoundMutation,
} = homeApi;
